import React, { useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/Button/Button3";
import Seo from "../Seo/Seo";
import "./ForgotPassword.css";
import homeBackground from '../../assets/images/home_background.avif';
import { ROUTES } from "../../utils/appRoutes";
import {
  buildAuthPathWithNext,
  getPostAuthFallbackPath,
  getSafeNextFromLocation,
} from "../../utils/authRedirect";
import { isValidEmail } from "../../utils/isValidEmail";
import { clearPasswordResetSession } from "../../utils/passwordResetSession";

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'success' | 'error'
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = getSafeNextFromLocation(location, getPostAuthFallbackPath(location));
  const resetPasswordPath = buildAuthPathWithNext(ROUTES.RESET_PASSWORD, nextPath);
  const loginPath = buildAuthPathWithNext(ROUTES.LOGIN, nextPath);
  const registerPath = buildAuthPathWithNext(ROUTES.REGISTER, nextPath);

  const handleForgotPassword = async () => {
    if (!email) {
      setMessage("Please enter your email.");
      setMessageType("error");
      return;
    }

    if (!isValidEmail(email)) {
      setMessage("Please enter a valid email address.");
      setMessageType("error");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/forgot-password`, { email });
      clearPasswordResetSession();
      setMessage(
        response.data.message ||
          "Password reset code sent successfully. Please check your inbox and spam folder."
      );
      setMessageType("success");

      navigate(resetPasswordPath, {
        state: {
          email,
          next: nextPath,
          resetRequestId: `${Date.now()}`,
        },
      });
    } catch (error) {
      setMessage(error.response?.data?.error || "Failed to send reset code.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Forgot Password | ZealFC"
        description="Request a password reset code for your ZealFC account."
        robots="noindex, nofollow"
        canonicalPath={ROUTES.FORGOT_PASSWORD}
      />
      <div className="forgot-password-layout">
        <div className="forgot-password-image-container">
          <img src={homeBackground} alt="Background" className="forgot-password-image" />
          <div className="noise-effect"></div>
        </div>
        <div className="forgot-password-form-container">
          <p className="forgot-password-title">FORGOT YOUR PASSWORD?</p>
          <form className="forgot-password-form" onSubmit={(e) => e.preventDefault()}>
            <div className="forgot-password-field">
              <span>Email:</span>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="forgot-password-input-line"
              />
            </div>
            <Button
              onClick={handleForgotPassword}
              variant="small"
              primaryText={loading ? "Sending..." : "Reset Code"}
              disabled={loading}
            />
          </form>
          {message && (
            <p className={`forgot-password-message ${messageType}`}>
              {message}
            </p>
          )}
          <div className="login-links">
            <div className="login-link">
              <span>Already have an account?</span>
              <Link to={loginPath} className="login-link-text"> Login</Link>
            </div>
            <div className="login-link">
              <span>Don't have an account?</span>
              <Link to={registerPath} className="login-link-text"> Register</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ForgotPassword;
